import React, { ReactNode, useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { get } from 'lodash';

import format from 'date-fns/format';
import { addMinutes, startOfDay } from 'date-fns';

import {
  Paper,
  Box,
  Tooltip,
  SxProps,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import {
  SpotPriceLevel,
  SpotPricePeriod,
} from '../../../core/src/shared/enums';
import { RelayControl } from '../shared';

import { Row } from '../atoms/row';
import { useSelector } from '../store/utils';
import { ControlBox } from '../atoms/control-box';
import { ParentPaper } from '../atoms/parent-paper';

const summaryHead = Array.from({ length: 24 }, (_, i) => i + 1);

const RELAY_SPOT_TYPES_60M: RelayControl[] = [
  RelayControl.SpotHi,
  RelayControl.SpotMi,
  RelayControl.SpotLow,
  RelayControl.SpotNegative,
  RelayControl.SpotPriceBelow,
  RelayControl.SpotPriceAbove,
];

const RELAY_SPOT_TYPES_15M: RelayControl[] = [
  RelayControl.SpotHi15M,
  RelayControl.SpotMi15M,
  RelayControl.SpotLow15M,
  RelayControl.SpotNegative15M,
  RelayControl.SpotPriceBelow15M,
  RelayControl.SpotPriceAbove15M,
];

type Props = {
  relayIndex: number;
};

const RowHead = ({ children, sx }: { children: ReactNode; sx: SxProps }) => (
  <Box sx={{ ...sx }}>{children}</Box>
);

const ColBoolean = ({
  pricePeriod = SpotPricePeriod['60M'],
  periodIndex,
  value,
}: {
  pricePeriod?: SpotPricePeriod;
  periodIndex: number;
  value: boolean;
}) => {
  return (
    <Tooltip
      title={
        <FormattedPriceIndex index={periodIndex} pricePeriod={pricePeriod} />
      }
    >
      <Box
        sx={{
          width: 'auto',
          height: 26,
          bgcolor: value ? 'primary.main' : 'grey.200',
          gridColumn: pricePeriod === '15M' ? 'span 1' : 'span 4',
        }}
      />
    </Tooltip>
  );
};

const ColPriceLevel = ({
  periodIndex,
  pricePeriod = SpotPricePeriod['60M'],
  value,
}: {
  periodIndex: number;
  pricePeriod?: SpotPricePeriod;
  value: SpotPriceLevel;
}) => {
  let title: ReactNode = null;
  let bgcolor = 'grey.200';

  if (value === SpotPriceLevel.Hi) {
    title = (
      <FormattedMessage
        id="spot.summary.price_level.hi"
        defaultMessage="Vysoká cena"
      />
    );
    bgcolor = 'error.main';
  } else if (value === SpotPriceLevel.Mid) {
    title = (
      <FormattedMessage
        id="spot.summary.price_level.mid"
        defaultMessage="Střední cena"
      />
    );
    bgcolor = '#ff9800';
  } else {
    title = (
      <FormattedMessage
        id="spot.summary.price_level.low"
        defaultMessage="Nízká cena"
      />
    );
    bgcolor = 'success.main';
  }

  return (
    <Tooltip
      title={
        <>
          {title} (
          <FormattedPriceIndex index={periodIndex} pricePeriod={pricePeriod} />)
        </>
      }
    >
      <Box
        sx={{
          width: 'auto',
          height: 26,
          bgcolor,
          gridColumn: pricePeriod === '15M' ? 'span 1' : 'span 4',
        }}
      />
    </Tooltip>
  );
};

const FormattedPriceIndex = ({
  index,
  pricePeriod,
}: {
  index: number;
  pricePeriod?: SpotPricePeriod;
}) => {
  const time = useMemo(() => {
    return format(
      addMinutes(
        startOfDay(new Date()),
        pricePeriod === SpotPricePeriod['15M'] ? index * 15 : index * 60
      ),
      'HH:mm'
    );
  }, [index, pricePeriod]);

  return <>{time}</>;
};

export const RelaySummary = ({ relayIndex }: Props) => {
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const sm = useMediaQuery(theme.breakpoints.up('sm'));

  const headSx: SxProps = {
    gridColumn: { xs: 'span 96', lg: 'auto' },
    fontSize: { xs: 12, lg: 14 },
    lineHeight: { xs: '12px', lg: '24px' },
    mt: { xs: 1.25, lg: 0 },
    mb: { xs: 0.5, lg: 0 },
    pr: { xs: 0, lg: 1 },
    width: 'auto',
  };

  const [
    prices,
    priceLevels,
    controlType,
    priceBelowRelay,
    priceAboveRelay,
    spotOverflow,
  ] = useSelector((state) => {
    const { variables } = state;
    return [
      variables.SP_HOURS_PRICE,
      variables.SP_PRICE_LEVEL,
      get(variables, `RELAY_CONTROL_TYPE[0][${relayIndex}]`) as
        | RelayControl
        | undefined,
      variables.SPOT_PRICE_BELOW_RELAY?.[relayIndex],
      variables.SPOT_PRICE_ABOVE_RELAY?.[relayIndex],
      variables.SP_OVERFLOW_TABLE,
    ] as const;
  });

  const schedule = useMemo(() => {
    const result: boolean[] = [];
    if (!RELAY_SPOT_TYPES_60M.includes(controlType as RelayControl)) {
      return result;
    }
    const type = controlType as RelayControl;

    for (let hour = 0; hour < 24; hour++) {
      let on = false;
      const level = priceLevels?.[hour];
      const price = prices?.[hour];

      if (type === RelayControl.SpotHi && level === SpotPriceLevel.Hi)
        on = true;
      if (type === RelayControl.SpotMi && level === SpotPriceLevel.Mid)
        on = true;
      if (type === RelayControl.SpotLow && level === SpotPriceLevel.Low)
        on = true;
      if (
        type === RelayControl.SpotPriceBelow &&
        priceBelowRelay != null &&
        price != null &&
        price <= priceBelowRelay
      ) {
        on = true;
      }
      if (
        type === RelayControl.SpotPriceAbove &&
        priceAboveRelay != null &&
        price != null &&
        price >= priceAboveRelay
      ) {
        on = true;
      }
      if (
        type === RelayControl.SpotNegative &&
        Array.isArray(spotOverflow) &&
        !spotOverflow[hour]
      ) {
        on = true;
      }
      result.push(on);
    }
    return result;
  }, [
    controlType,
    prices,
    priceLevels,
    priceBelowRelay,
    priceAboveRelay,
    spotOverflow,
  ]);

  if (!prices || prices.length !== 24) return null;
  if (!RELAY_SPOT_TYPES_60M.includes(controlType as RelayControl)) return null;

  return (
    <ParentPaper>
      <ControlBox
        title={
          <FormattedMessage
            id="relay.spot.diagram"
            defaultMessage="Spínání podle spotové ceny"
          />
        }
        fullWidth
        visibility={{ variableId: `RELAY_ENABLED[${relayIndex}]` }}
      />
      <Paper sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(96, 1fr)',
              lg: 'auto repeat(96, 1fr)',
            },
            rowGap: { lg: 1 },
          }}
        >
          <Box sx={{ ...headSx }}>
            <FormattedMessage id="plug.summary.time" defaultMessage="Čas" />
          </Box>
          {summaryHead.map((i) => {
            if (!lg && i % 2 === 0) return null;
            if (!sm && i % 3 === 0) return null;
            return (
              <Box
                component="span"
                key={i}
                sx={{
                  textAlign: 'left',
                  fontSize: 12,
                  gridColumn: { xs: 'span 12', sm: 'span 8', lg: 'span 4' },
                  width: 'auto',
                }}
              >
                {i - 1}:00
              </Box>
            );
          })}

          <Box sx={{ ...headSx }}>
            <FormattedMessage id="plug.summary.price" defaultMessage="Cena" />
          </Box>
          {prices.map((value, i) => {
            if (!lg && (i + 1) % 2 === 0) return null;
            if (!sm && (i + 1) % 3 === 0) return null;
            return (
              <Box
                key={i}
                component="span"
                sx={{
                  fontSize: 12,
                  gridColumn: { xs: 'span 12', sm: 'span 8', lg: 'span 4' },
                  width: 'auto',
                }}
              >
                <FormattedNumber
                  value={value}
                  maximumFractionDigits={2}
                  minimumFractionDigits={2}
                />
              </Box>
            );
          })}

          <RowHead sx={headSx}>
            <FormattedMessage
              id="plug.summary.price_level"
              defaultMessage="Hladina ceny"
            />
          </RowHead>
          {priceLevels?.map((value, i) => (
            <ColPriceLevel value={value} key={i} periodIndex={i} />
          ))}

          <RowHead sx={headSx}>
            <FormattedMessage
              id="relay.summary.switch"
              defaultMessage="Sepnutí relé"
            />
          </RowHead>
          {schedule.map((value, i) => (
            <ColBoolean
              key={i}
              value={value}
              periodIndex={i}
              pricePeriod={SpotPricePeriod['60M']}
            />
          ))}
        </Box>
      </Paper>
    </ParentPaper>
  );
};

export const RelaySummary15Min = ({ relayIndex }: Props) => {
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const sm = useMediaQuery(theme.breakpoints.up('sm'));

  const headSx: SxProps = {
    gridColumn: { xs: 'span 96', lg: 'auto' },
    fontSize: { xs: 12, lg: 14 },
    lineHeight: { xs: '12px', lg: '24px' },
    mt: { xs: 1.25, lg: 0 },
    mb: { xs: 0.5, lg: 0 },
    pr: { xs: 0, lg: 1 },
    width: 'auto',
  };

  const [
    prices,
    priceLevels15M,
    controlType,
    priceBelowRelay15M,
    priceAboveRelay15M,
    spotOverflow15M,
  ] = useSelector((state) => {
    const { variables } = state;
    return [
      variables.SP_15M_PRICE,
      variables.SP_PRICE_15M_LEVEL,
      get(variables, `RELAY_CONTROL_TYPE[0][${relayIndex}]`) as
        | RelayControl
        | undefined,
      variables.SPOT_PRICE_BELOW_RELAY_15MIN?.[relayIndex],
      variables.SPOT_PRICE_ABOVE_RELAY_15MIN?.[relayIndex],
      variables.SP_OVERFLOW_15M_TABLE,
    ] as const;
  });

  const schedule15M = useMemo(() => {
    const result: boolean[] = [];
    if (!RELAY_SPOT_TYPES_15M.includes(controlType as RelayControl)) {
      return result;
    }
    const type = controlType as RelayControl;

    for (let index = 0; index < 96; index++) {
      let on = false;
      const level = priceLevels15M?.[index];
      const price = prices?.[index];

      if (type === RelayControl.SpotHi15M && level === SpotPriceLevel.Hi)
        on = true;
      if (type === RelayControl.SpotMi15M && level === SpotPriceLevel.Mid)
        on = true;
      if (type === RelayControl.SpotLow15M && level === SpotPriceLevel.Low)
        on = true;
      if (
        type === RelayControl.SpotPriceBelow15M &&
        priceBelowRelay15M != null &&
        price != null &&
        price <= priceBelowRelay15M
      ) {
        on = true;
      }
      if (
        type === RelayControl.SpotPriceAbove15M &&
        priceAboveRelay15M != null &&
        price != null &&
        price >= priceAboveRelay15M
      ) {
        on = true;
      }
      if (
        type === RelayControl.SpotNegative15M &&
        Array.isArray(spotOverflow15M) &&
        !spotOverflow15M[index]
      ) {
        on = true;
      }
      result.push(on);
    }
    return result;
  }, [
    controlType,
    prices,
    priceLevels15M,
    priceBelowRelay15M,
    priceAboveRelay15M,
    spotOverflow15M,
  ]);

  if (!prices || prices.length !== 96) return null;
  if (!RELAY_SPOT_TYPES_15M.includes(controlType as RelayControl)) return null;

  return (
    <ParentPaper>
      <ControlBox
        title={
          <FormattedMessage
            id="relay.spot.diagram"
            defaultMessage="Spínání podle spotové ceny"
          />
        }
        fullWidth
      />
      <Paper sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(96, 1fr)',
              lg: 'auto repeat(96, 1fr)',
            },
            rowGap: { lg: 1 },
          }}
        >
          <Box sx={{ ...headSx }}>
            <FormattedMessage id="plug.summary.time" defaultMessage="Čas" />
          </Box>
          {summaryHead.map((i) => {
            if (!lg && i % 2 === 0) return null;
            if (!sm && i % 3 === 0) return null;
            return (
              <Box
                component="span"
                key={i}
                sx={{
                  textAlign: 'left',
                  fontSize: 12,
                  gridColumn: { xs: 'span 12', sm: 'span 8', lg: 'span 4' },
                  width: 'auto',
                }}
              >
                {i - 1}:00
              </Box>
            );
          })}

          <RowHead sx={headSx}>
            <FormattedMessage
              id="plug.summary.price_level_15m"
              defaultMessage="Hladina ceny 15m"
            />
          </RowHead>
          {priceLevels15M?.map((value, i) => (
            <ColPriceLevel
              value={value}
              key={i}
              periodIndex={i}
              pricePeriod={SpotPricePeriod['15M']}
            />
          ))}

          <RowHead sx={headSx}>
            <FormattedMessage
              id="relay.summary.switch"
              defaultMessage="Sepnutí relé"
            />
          </RowHead>
          {schedule15M.map((value, i) => (
            <ColBoolean
              key={i}
              value={value}
              periodIndex={i}
              pricePeriod={SpotPricePeriod['15M']}
            />
          ))}
        </Box>
      </Paper>
    </ParentPaper>
  );
};
